import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  delay?: number;
  position?: 'right' | 'left' | 'top' | 'bottom';
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  delay = 200,
  position = 'right',
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const timeoutRef = useRef<NodeJS.Timeout>();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const showTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  useEffect(() => {
    if (isVisible && tooltipRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = 0;
      let left = 0;

      switch (position) {
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.right + 12;
          
          // Vérifier si le tooltip dépasse à droite
          if (left + tooltipRect.width > viewportWidth) {
            left = triggerRect.left - tooltipRect.width - 12;
          }
          
          // Vérifier si le tooltip dépasse en haut ou en bas
          if (top < 8) {
            top = 8;
          } else if (top + tooltipRect.height > viewportHeight - 8) {
            top = viewportHeight - tooltipRect.height - 8;
          }
          break;
          
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.left - tooltipRect.width - 12;
          
          if (left < 8) {
            left = triggerRect.right + 12;
          }
          
          if (top < 8) {
            top = 8;
          } else if (top + tooltipRect.height > viewportHeight - 8) {
            top = viewportHeight - tooltipRect.height - 8;
          }
          break;
          
        case 'top':
          top = triggerRect.top - tooltipRect.height - 12;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          
          if (top < 8) {
            top = triggerRect.bottom + 12;
          }
          
          if (left < 8) {
            left = 8;
          } else if (left + tooltipRect.width > viewportWidth - 8) {
            left = viewportWidth - tooltipRect.width - 8;
          }
          break;
          
        case 'bottom':
          top = triggerRect.bottom + 12;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          
          if (top + tooltipRect.height > viewportHeight - 8) {
            top = triggerRect.top - tooltipRect.height - 12;
          }
          
          if (left < 8) {
            left = 8;
          } else if (left + tooltipRect.width > viewportWidth - 8) {
            left = viewportWidth - tooltipRect.width - 8;
          }
          break;
      }

      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999,
      });
    }
  }, [isVisible, position]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        className={className}
      >
        {children}
      </div>
      
      {isVisible && (
        <div
          ref={tooltipRef}
          style={tooltipStyle}
          className="bg-gray-900 bg-opacity-95 text-white text-sm px-3 py-2 rounded-lg shadow-lg pointer-events-none animate-in fade-in-0 zoom-in-95 duration-200"
        >
          {content}
          <div className="absolute w-2 h-2 bg-gray-900 bg-opacity-95 rotate-45 -translate-x-1/2 -translate-y-1/2"
               style={{
                 left: position === 'right' ? '-4px' : position === 'left' ? 'calc(100% + 4px)' : '50%',
                 top: position === 'right' || position === 'left' ? '50%' : 
                      position === 'top' ? 'calc(100% + 4px)' : '-4px'
               }}
          />
        </div>
      )}
    </>
  );
};